import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Row,
  Col,
  Button,
} from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatPrice, parsePrice } from "../../../utils/priceUtils";

const LicenseFormModal = ({
  open,
  onCancel,
  onSubmit,
  isEdit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { company } = useSelector((state) => state.company);
  const currencyCode = company?.currency?.currency_code ?? "TND";
  const decimalPlaces = company?.currency?.decimal_places ?? 3;

  useEffect(() => {
    if (isEdit && initialData) {
      form.setFieldsValue({
        licenseName: initialData.licenseName,
        licenseDescription: initialData.licenseDescription,
        licenseMonthlyPriceIncludeTax:
          initialData.licenseMonthlyPriceIncludeTax,
        licenseYearlyPriceIncludeTax: initialData.licenseYearlyPriceIncludeTax,
        licenseMaxUsersNumber: initialData.licenseMaxUsersNumber,
        licenseMaxCustomersNumber: initialData.licenseMaxCustomersNumber,
        licenseIsActive: initialData.licenseIsActive ?? true,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ licenseIsActive: true });
    }
  }, [isEdit, initialData, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      form.resetFields();
      onSubmit(values);
    } catch {
      /* validation failed */
    }
  };

  return (
    <Modal
      title={isEdit ? t("plans.editPlan") : t("plans.addPlan")}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
      width={700}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="license_name"
              label={t("plans.name")}
              rules={[{ required: true, message: t("plans.requiredName") }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="license_is_active"
              label={t("plans.status")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="license_description" label={t("plans.description")}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="license_monthly_price_include_tax"
              label={t("plans.monthlyPrice")}
              rules={[{ required: true, message: t("plans.priceRequired") }]}
            >
              <InputNumber
                min={0}
                step={0.001}
                style={{ width: "100%" }}
                addonAfter={currencyCode}
                formatter={(value) => formatPrice(value, decimalPlaces)}
                parser={(value) => parsePrice(value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="license_yearly_price_include_tax"
              label={t("plans.yearlyPrice")}
              rules={[{ required: true, message: t("plans.priceRequired") }]}
            >
              <InputNumber
                min={0}
                step={0.001}
                style={{ width: "100%" }}
                addonAfter={currencyCode}
                formatter={(value) => formatPrice(value, decimalPlaces)}
                parser={(value) => parsePrice(value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="license_max_users_number"
              label={t("plans.maxUsers")}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="license_max_customers_number"
              label={t("plans.maxCustomers")}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <div className="d-flex flex-row-reverse gap-3 mt-3">
          <Button type="primary" onClick={handleOk}>
            {isEdit ? t("common.save") : t("common.add")}
          </Button>
          <Button onClick={onCancel}>{t("common.cancel")}</Button>
        </div>
      </Form>
    </Modal>
  );
};

LicenseFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  initialData: PropTypes.object,
};

export default LicenseFormModal;
